import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import { Notifications } from './components/Layout/Notifications'
// Removed unused ProtectedRoute and GuestRoute; loaders handle auth
import { createBrowserRouter, RouterProvider, redirect } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { AuthPage } from '@/components/Auth/AuthPage'
import { ProtectedApp } from '@/routes/ProtectedApp'
import { PrivacyPolicy } from '@/routes/PrivacyPolicy'
import { TermsOfService } from '@/routes/TermsOfService'
import { Pricing } from '@/routes/Pricing'
import { Contact } from '@/routes/Contact'
import { initializeTheme } from './lib/theme'
// Removed Toaster; using global Notifications bottom-left
import { AcceptInvite } from '@/components/Auth/AcceptInvite'
import { ForgotPasswordPage } from '@/components/Auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/components/Auth/ResetPasswordPage'
import { apiFetch } from '@/lib/api'
import { AppErrorBoundary } from '@/components/Layout/AppErrorBoundary'
import { RouteErrorBoundary } from '@/components/Layout/RouteErrorBoundary'
import { ShareLinkAccess, ResearchView, ProposalView } from '@/routes/public/share'
import { FlightCostView } from '@/routes/public/share/FlightCostView'

function trackNavigation(_path: string) {}

// Initialize theme on app load
initializeTheme()

// Initialize VConsole mobile console for development and debugging
const shouldEnableVConsole = import.meta.env.DEV || 
                            import.meta.env.MODE === 'development' || 
                            import.meta.env.VITE_ENABLE_MOBILE_DEBUG === 'true';

if (shouldEnableVConsole) {
  import('vconsole').then((VConsole) => {
    new VConsole.default({
      theme: 'dark',
      defaultPlugins: ['system', 'network', 'element', 'storage'],
      maxLogNumber: 1000,
      onReady: () => {
        console.log('📱 VConsole mobile debugger initialized');
        console.log('📱 Tap the green button to open the mobile console');
      }
    });
  }).catch(error => {
    console.warn('Failed to load VConsole mobile debugger:', error);
  });
}


const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    loader: async () => {
      trackNavigation('/');
      try {
        const res = await apiFetch('/api/session/bootstrap', { skipRefresh: true });
        if (res.ok) {
          const payload = await res.json().catch(() => ({ data: {} }));
          const active = payload?.data?.activeOrgId || null;
          // Persist active org for services that read from localStorage (for X-Organization-Id header)
          try {
            if (active) localStorage.setItem('ima_active_org_id', active);
          } catch {}
          let completed = false;
          if (active) {
            try { completed = localStorage.getItem(`ima_onboarding_completed_${active}`) === '1'; } catch {}
          }
          // User is logged in, redirect to dashboard or onboarding
          const destination = completed ? '/dashboard' : '/onboarding';
          throw redirect(destination);
        }
      } catch (e) {
        if (e instanceof Response) {
          // This is a redirect, re-throw it
          throw e;
        }
      }
      // User is not logged in, redirect to auth
      throw redirect('/auth');
    },
  },
  {
    path: '/pricing',
    element: <Pricing />,
  },
  {
    path: '/contact',
    element: <Contact />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms',
    element: <TermsOfService />,
  },
  {
    path: '/accept-invite',
    element: <AcceptInvite />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/auth',
    errorElement: <RouteErrorBoundary />,
    loader: async () => {
      trackNavigation('/auth');
      try {
        const res = await apiFetch('/api/session/bootstrap', { skipRefresh: true });
        if (res.ok) {
          const payload = await res.json().catch(() => ({ data: {} }));
          const active = payload?.data?.activeOrgId || null;
          // Persist active org for services that read from localStorage (for X-Organization-Id header)
          try {
            if (active) localStorage.setItem('ima_active_org_id', active);
          } catch {}
          let completed = false;
          if (active) {
            try { completed = localStorage.getItem(`ima_onboarding_completed_${active}`) === '1'; } catch {}
          }
          const destination = completed ? '/dashboard' : '/onboarding';
          throw redirect(destination);
        }
      } catch (e) {
        if (e instanceof Response) {
          // This is a redirect, re-throw it
          throw e;
        }
      }
      return null;
    },
    element: <AuthPage />,
  },
  // Public share links - no authentication required
  {
    path: '/r/:token',
    element: <ShareLinkAccess linkType="research" />,
  },
  {
    path: '/r/:token/view',
    element: <ResearchView />,
  },
  {
    path: '/p/:token',
    element: <ShareLinkAccess linkType="proposal" />,
  },
  {
    path: '/p/:token/view',
    element: <ProposalView />,
  },
  {
    path: '/f/:token',
    element: <FlightCostView />,
  },
  {
    path: '*',
    errorElement: <RouteErrorBoundary />,
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const pathname = url.pathname;
      trackNavigation(pathname);
      
      // Skip bootstrap for static assets
      if (pathname.includes('.') || pathname.startsWith('/assets/')) {
        return null;
      }
      
      // Skip bootstrap for public share links - these should never redirect to /auth
      if (pathname.startsWith('/r/') || pathname.startsWith('/p/') || pathname.startsWith('/f/')) {
        return null;
      }
      
      try {
        // Allow automatic refresh on protected routes when access token expires
        // skipRedirect allows us to handle the redirect manually in the loader
        const res = await apiFetch('/api/session/bootstrap', { 
          skipRedirect: true
        });
        if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Bootstrap response:', res.status, res.ok);
        
        if (!res.ok) {
          if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Not authenticated, redirecting to /auth');
          // Clear auth store to prevent stale data
          const { useAuthStore } = await import('@/stores/authStore');
          useAuthStore.setState({ user: null });
          throw redirect('/auth');
        }
        
        const payload = await res.json().catch(() => ({ data: {} }));
        const { user, orgs, activeOrgId } = payload?.data || {};
        // Persist active org for services that read from localStorage (for X-Organization-Id header)
        try {
          if (activeOrgId) localStorage.setItem('ima_active_org_id', activeOrgId);
        } catch {}
        
        // Initialize org store with bootstrap data to prevent redundant fetching
        if (orgs && Array.isArray(orgs)) {
          const { useOrgStore } = await import('@/stores/orgStore');
          const currentState = useOrgStore.getState();
          
          // Only update if data is different to prevent re-renders
          if (!currentState.initialized || currentState.orgs.length !== orgs.length) {
            useOrgStore.setState({ 
              orgs, 
              activeOrgId: activeOrgId || null,
              initialized: true,
              isLoading: false,
              error: null
            });
          }
        }
        
        if (activeOrgId) {
          const storageKey = `ima_onboarding_completed_${activeOrgId}`;
          let storageValue = null;
          try { storageValue = localStorage.getItem(storageKey); } catch {}
          
          // Check both localStorage and org data
          const orgData = orgs?.find((o: any) => o.id === activeOrgId);
          const completedFromOrg = Boolean(orgData?.onboarding_completed);
          const completedFromStorage = storageValue === '1';
          const completed = completedFromOrg || completedFromStorage;
          
          if (!completed && pathname !== '/onboarding') {
            throw redirect('/onboarding');
          }
          if (completed && pathname === '/onboarding') {
            throw redirect('/dashboard');
          }
        }
        
        return user || null;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Caught error:', e);
        if (e instanceof Response) {
          // This is a redirect, re-throw it
          if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Re-throwing redirect');
          throw e;
        }
        if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Non-redirect error, redirecting to /auth');
        throw redirect('/auth');
      }
    },
    element: <ProtectedApp />,
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors like 403 Forbidden)
        // Only retry on network errors and 5xx server errors
        if (error?.message?.includes('403') || error?.message?.includes('401') || error?.message?.includes('404')) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Notifications />
        </QueryClientProvider>
      </AppErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>,
)



